import React, {useEffect, useState} from 'react';
import {Form} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import {MAX_SELECTED_CITIES} from '../../generatorOptions';

const CitySearch = ({query, results, selected, disabled, onQueryChange, onAdd, onRemove}) => {
    const {t} = useTranslation();
    const [focused, setFocused] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const atLimit = selected.length >= MAX_SELECTED_CITIES;
    const showResults = focused && !disabled && !atLimit && query && results.length > 0;
    const options = results.flatMap((feature, featureIndex) => feature.properties.names.map((name, nameIndex) => ({
        feature, name, id: `city-option-${featureIndex}-${nameIndex}`,
    })));
    const activeOption = showResults ? options[activeIndex] : undefined;

    useEffect(() => setActiveIndex(-1), [results, query]);
    useEffect(() => {
        if (activeOption) document.getElementById(activeOption.id)?.scrollIntoView({block: 'nearest'});
    }, [activeOption?.id]);

    return (
        <Form.Group className="mb-3 position-relative" onBlur={event => {
            if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
        }}>
            <Form.Label htmlFor="city">{t('generator.location.city')}</Form.Label>
            <Form.Control
                id="city"
                maxLength={128}
                className="city-search"
                autoComplete="off"
                placeholder={t('generator.location.city.placeholder')}
                value={query}
                disabled={disabled || atLimit}
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={Boolean(showResults)}
                aria-controls={showResults ? 'city-results' : undefined}
                aria-activedescendant={activeOption?.id}
                aria-describedby={selected.length ? 'city-selection-count' : undefined}
                onChange={event => {
                    setFocused(true);
                    onQueryChange(event);
                }}
                onFocus={() => setFocused(true)}
                onKeyDown={event => {
                    if (event.key === 'Escape') {
                        setFocused(false);
                        setActiveIndex(-1);
                        return;
                    }
                    if (disabled || atLimit || !query || !options.length) return;
                    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                        event.preventDefault();
                        setFocused(true);
                        setActiveIndex(index => event.key === 'ArrowDown' ? (index + 1) % options.length : (index < 0 ? options.length - 1 : (index - 1 + options.length) % options.length));
                    } else if (event.key === 'Enter' && activeOption) {
                        event.preventDefault();
                        onAdd(activeOption.feature, activeOption.name);
                    }
                }}
            />
            {showResults && (
                <div id="city-results" role="listbox" className="dropdown-menu show design-menu city-results w-100 mt-2">
                    {options.map(({feature, name, id}, index) => (
                        <React.Fragment key={id}>
                            {(index === 0 || feature !== options[index - 1].feature) && <div className="dropdown-header">{feature.properties.display_name}</div>}
                            <button
                                className="dropdown-item"
                                type="button"
                                id={id}
                                role="option"
                                aria-selected={activeIndex === index}
                                tabIndex={-1}
                                onMouseDown={event => event.preventDefault()}
                                onClick={() => onAdd(feature, name)}
                            >
                                {name}
                            </button>
                        </React.Fragment>
                    ))}
                </div>
            )}
            {selected.length > 0 && (
                <>
                    <div className="selected-cities">
                        {selected.map((feature, index) => (
                            <button
                                key={`${feature.geometry.coordinates.join(',')}:${feature.properties.name}`}
                                type="button"
                                className="selected-city"
                                aria-label={t('generator.location.city.remove', {name: feature.properties.name})}
                                disabled={disabled}
                                onClick={() => onRemove(index)}
                            >
                                <span>{feature.properties.name}</span>
                                <span className="selected-city-remove" aria-hidden="true">×</span>
                            </button>
                        ))}
                    </div>
                    <p id="city-selection-count" className="city-selection-count" aria-live="polite">{selected.length} / {MAX_SELECTED_CITIES}</p>
                </>
            )}
        </Form.Group>
    );
};

export default CitySearch;
